import { useFormik } from 'formik';
import { generatePath, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import ListHeader from 'shell/components/shared/list-header';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import SubscriberFilterForm from 'system/components/subscriber/list/filters';
import { initialValuesSubscriber } from 'system/pages/subscriber/index';
import { subscriberCrumbs } from '../constants';
import { authsObj, validationSchemaSubscriberFilterForm } from './constants';
import { subscriberForm, TypeSubscriberClient } from './types';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import Table from 'shell/components/shared/table';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { headCells } from './constants';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { HttpResponseList } from 'shell/models/http-response';
import { SubscriberResponse, transformerSubscriber } from '../utils/transform-subscribers';
import { __OPERATION__EVENT__ADMIN__ } from 'shell/constants/environment';
import { Subscriber } from 'shell/models/subscriber';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { transformDataset } from './utils';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { AxiosPromise } from 'axios';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Typography } from '@mui/material';

const SubscriberListPage = (): React.ReactElement => {
  const auths = useActionAuthorization(authsObj);
  const navigate = useNavigate();
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<TypeSubscriberClient>>({
    element: null,
    open: false,
  });
  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<TypeSubscriberClient>>({
    element: null,
    open: false,
  });

  const [subscribers, getSubscribers] = useHttpFetch<
    HttpResponseList<Subscriber>,
    SubscriberResponse
  >(`${__OPERATION__EVENT__ADMIN__}/sottoscrittore/list`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerSubscriber,
  });

  const deleteSubscriber = useHttpClient(`${__OPERATION__EVENT__ADMIN__}/sottoscrittore`, {
    options: {
      method: 'DELETE',
    },
  });

  const onSubmit = useCallback(
    (formValues: subscriberForm) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      subscriberFormFormik.setFieldValue('page', 0);
      getSubscribers({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getSubscribers]
  );
  const subscriberFormFormik = useFormik({
    initialValues: initialValuesSubscriber,
    validationSchema: validationSchemaSubscriberFilterForm,
    onSubmit,
  });

  const filterTable = useFilterTable<subscriberForm>();
  useEffect(() => {
    subscriberFormFormik.setValues(filterTable.values || initialValuesSubscriber);
    const queryString = transformDataset(filterTable.values || initialValuesSubscriber);
    getSubscribers({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = { ...subscriberFormFormik.values, page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      (
        getSubscribers({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<Subscriber>, SubscriberResponse>
      ).then(() => {
        subscriberFormFormik.setFieldValue('page', newPage);
      });
    },
    [getSubscribers, subscriberFormFormik.values, subscriberFormFormik.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...subscriberFormFormik.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      const queryString = transformDataset(newDataset);
      (
        getSubscribers({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<Subscriber>, SubscriberResponse>
      ).then(() => {
        subscriberFormFormik.setValues(newDataset);
      });
    },
    [getSubscribers, subscriberFormFormik.setFieldValue, subscriberFormFormik.values]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<TypeSubscriberClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/eventmanager/sottoscrittore/dettaglio/:id', {
            id: cell.id ? cell.id : cell.code,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<TypeSubscriberClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/eventmanager/sottoscrittore/modifica/:id', {
            id: cell.id ? cell.id : cell.code,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<TypeSubscriberClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<TypeSubscriberClient>;
    },
    [setDeleteAlertModal]
  );

  const count: number = useMemo(() => {
    return (subscribers.data as SubscriberResponse)?.pageInfo?.totalCount || 0;
  }, [subscribers.data]);

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...subscriberFormFormik.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getSubscribers({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<Subscriber>, SubscriberResponse>
      ).then(() => {
        subscriberFormFormik.setFieldValue('orders', orders);
      });
    },
    [getSubscribers, subscriberFormFormik.values, subscriberFormFormik.setValues]
  );

  return (
    <>
      <Breadcrumbs items={subscriberCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo Sottoscrittore"
        addBtnLabel="Aggiungi nuovo Sottoscrittore"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/eventmanager/sottoscrittore/nuovo');
        }}
        title="Gestione Sottoscrittore"
      />
      <SubscriberFilterForm subscriberFormProps={subscriberFormFormik} />
      {(subscribers.data as SubscriberResponse)?.subscribers?.length > 0 ? (
        <Table
          onSort={onSort}
          head={headCells}
          orders={subscriberFormFormik.values.orders}
          activeActions={auths}
          page={subscriberFormFormik.values.page}
          rowsPerPage={subscriberFormFormik.values.rowsPerPage}
          body={(subscribers.data as SubscriberResponse)?.subscribers}
          onVisibilityPress={onVisibilityPress}
          onEditPress={onEditPress}
          onTrashPress={onTrashPress}
          count={count || 0}
          isServer
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ariaLabel="Lista Sottoscrittore"
        />
      ) : (
        <Typography color="secondary.main">
          Nessun elemento trovato con la ricerca effettuata.
        </Typography>
      )}
      <DeleteAlertModal
        title="Cancellazione Sottoscrittore"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteSubscriber({
              url: `/${
                deleteAlertModal.element?.id
                  ? deleteAlertModal.element.id
                  : deleteAlertModal.element?.code
              }`,
            }) as AxiosPromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Sottoscrittore"
        message={`La cancellazione del Sottoscrittore ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          setDeleteSuccessModal({ open: false, element: null });
          const newDataset = {
            ...subscriberFormFormik.values,
            page: 0,
          };
          const queryString = transformDataset(newDataset);
          getSubscribers({
            params: {},
            paramsSerializer: () => queryString,
          });
        }}
        onOk={() => {
          setDeleteSuccessModal({ open: false, element: null });
          const newDataset = {
            ...subscriberFormFormik.values,
            page: 0,
          };
          const queryString = transformDataset(newDataset);
          getSubscribers({
            params: {},
            paramsSerializer: () => queryString,
          });
        }}
      />
    </>
  );
};
SubscriberListPage.displayName = 'Subscriber list page component';
export default SubscriberListPage;
